/**
 * Tipos compartidos entre el servidor y el panel.
 * Todo lo que viaja por la red está descrito aquí.
 */
import type { AppsLook, AppTheme } from "./brand.ts";

/**
 * Tipos de columna que puede crear el usuario.
 *
 * Apuntar a otra tabla es una sola cosa, `relation`, se apunte a donde se
 * apunte. La tabla de personas de la aplicación es una tabla destino más: no
 * hay un tipo aparte para ella.
 */
export type FieldType =
  | "text"
  | "longtext"
  | "number"
  | "bool"
  | "email"
  | "url"
  | "date"
  | "select"
  | "file"
  | "relation";

/**
 * Los mismos tipos, para comprobarlos en tiempo de ejecución.
 *
 * Hace falta donde un tipo llega de fuera --lo que propone la IA, lo que
 * contesta el diálogo de impacto-- y el tipo de TypeScript ya no está. El
 * `satisfies` es lo que hace que añadir un tipo arriba y olvidarse de esta
 * lista no compile.
 */
export const FIELD_TYPES = [
  "text",
  "longtext",
  "number",
  "bool",
  "email",
  "url",
  "date",
  "select",
  "file",
  "relation",
] as const satisfies readonly FieldType[];

/** Si un valor cualquiera nombra un tipo de columna. */
export function isFieldType(value: unknown): value is FieldType {
  return typeof value === "string" && (FIELD_TYPES as readonly string[]).includes(value);
}

export interface FieldDef {
  /** Id interno de la columna. Lo asigna el servidor; permite renombrar sin perder datos. */
  id?: string;
  /** Nombre técnico dentro de la tabla (sin espacios). */
  name: string;
  /** Nombre que ve el usuario. */
  label: string;
  type: FieldType;
  required?: boolean;
  /** Opciones para el tipo `select`. */
  options?: string[];
  /** Permitir varias opciones / varios archivos. */
  multiple?: boolean;
  /** Id de la tabla destino para el tipo `relation`. */
  relationTableId?: string;
  /**
   * Nombre técnico de la columna de la tabla destino que se enseña en lugar del
   * id. Vale para la cuadrícula, la exportación y la página publicada. Solo para
   * `relation`.
   *
   * Vacío en las columnas de antes de este cambio: hasta que la migración la
   * rellena, se cae a la primera columna única del destino, que es lo que la
   * cuadrícula adivinaba antes.
   */
  displayField?: string;
  /**
   * Nombre técnico de la columna del destino que se ve entre paréntesis, detrás
   * de la que se enseña. Solo para `relation`.
   *
   * Existe porque la llave con la que se reconoce a alguien y la que dice quien
   * es no suelen ser la misma: la celda enseña la cédula --que es lo que trae
   * el archivo y lo que se escribe-- y el correo al lado dice de quien se
   * trata, sin obligar a elegir entre las dos.
   *
   * Vacío: no se enseña nada detrás.
   */
  detailField?: string;
  /**
   * Nadie puede repetir su valor en esta tabla. Solo una columna única sirve de
   * llave para emparejar: si dos personas se llaman igual, el nombre no
   * identifica a nadie.
   */
  unique?: boolean;
  /**
   * Qué pasa con las filas de esta columna cuando se borra aquello a lo que
   * apuntan. Solo para `relation`. Vacío: `keep`.
   *
   * No se pregunta al crear la columna --ahí ya se piden tres cosas, y esta es
   * sobre algo que quizá no pase nunca-- sino en su tarjeta. Ver `design.md`
   * D7 de `relacion-automatica-con-personas`.
   */
  onDelete?: RelationOnDelete;
  /**
   * Columna del sistema: la sostiene la plataforma y no el constructor.
   *
   * Su valor no está en la colección de la tabla sino en otra --la cuenta común
   * o el enlace persona-aplicación-- y se superpone al pintar. Marcarla es lo
   * que decide dos cosas a la vez: que no se pueda borrar, renombrar ni cambiar
   * de tipo, y por que camino escribe su celda. Ver `shared/people.ts`.
   *
   * Vacío en cualquier columna que haya puesto el constructor.
   */
  system?: SystemFieldKind;
}

/** Que dato del sistema sostiene una columna marcada. Ver `shared/people.ts`. */
export type SystemFieldKind = "cuenta" | "roles";

/**
 * Que se hace con las filas que señalaban a un registro que se borra.
 *
 * - `keep`: la fila se queda y su celda sigue enseñando lo que decía, ahora
 *   como valor sin dueño. Es lo que vale cuando la columna no dice nada.
 * - `cascade`: las filas que apuntaban se borran con el.
 *
 * No hay una tercera que vacíe la celda dejando la fila, que es lo que hacía
 * PocketBase por su cuenta sin que nadie lo eligiera: pierde el dato y no da
 * nada a cambio.
 */
export type RelationOnDelete = "keep" | "cascade";

/** Que hace una columna al borrarse lo apuntado, con su respaldo. */
export function onDeleteOf(field: Pick<FieldDef, "onDelete">): RelationOnDelete {
  return field.onDelete === "cascade" ? "cascade" : "keep";
}

/**
 * Lo que se lleva por delante borrar unas filas, por tabla.
 *
 * Se pregunta al servidor antes de confirmar: quien borra tiene que poder saber
 * lo que va a pasar con números de verdad, no con un "no se puede deshacer".
 */
export interface DeleteImpact {
  /** Se borran con el registro: su columna está declarada en cascada. */
  cascade: { table: string; rows: number }[];
  /** Se quedan, con el valor a la vista y sin enlace. */
  keep: { table: string; rows: number }[];
}

/**
 * Una columna de relación ocupa dos columnas reales en la base: la relación
 * (el id, o vacío) y el valor sin dueño (texto, o vacío). Nunca están las dos
 * llenas a la vez.
 *
 * El texto no es una copia de lo que se enseña --eso se lee siempre del registro
 * enlazado-- sino el campo de reserva de lo que no encontró dueño. En una tabla sana
 * está vacío en todas las filas.
 */
export const ORPHAN_SUFFIX = "_sin_enlace";

/** Nombre real de la columna que guarda el valor sin dueño de una relación. */
export function orphanFieldName(fieldName: string): string {
  return `${fieldName}${ORPHAN_SUFFIX}`;
}

/**
 * El nombre técnico que sale de un título escrito a mano.
 *
 * Vive aquí y no en `server/schema.ts`, que es quien lo aplica, porque la
 * tarjeta de columna necesita saber en que se va a convertir lo que se está
 * escribiendo antes de mandarlo. Con una copia en cada lado, el aviso de la
 * pantalla y el rechazo del servidor acabarian hablando de nombres distintos.
 */
export function slugify(value: string, fallback = "item"): string {
  const out = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return out || fallback;
}

/** Nombre técnico válido para una columna o tabla de PocketBase. */
export function identifier(value: string, fallback = "campo"): string {
  const out = slugify(value, fallback)
    .replace(/-/g, "_")
    .replace(/^[0-9]+/, "");
  return out || fallback;
}

/** Una columna que apunta a un registro de otra tabla. */
export function isRelationField(field: Pick<FieldDef, "type">): boolean {
  return field.type === "relation";
}

/**
 * Que columna del destino enseña una relación.
 *
 * Vacío es "no lo declara": el respaldo no vive aquí porque depende de la tabla
 * destino --su primera columna única-- y esta función no la tiene delante. Lo
 * pone `relationTarget` en `shared/relations.ts`, que si la tiene.
 */
export function displayFieldOf(field: Pick<FieldDef, "displayField">): string {
  return field.displayField ?? "";
}

/**
 * Que columna del destino va entre paréntesis, si va alguna.
 *
 * La misma columna dos veces no dice nada, así que si coincide con la que se
 * enseña no se enseña detrás.
 */
export function detailFieldOf(field: Pick<FieldDef, "displayField" | "detailField">): string {
  const display = displayFieldOf(field);
  const detail = field.detailField ?? "";
  return detail && detail !== display ? detail : "";
}

/** Metadatos de presentación de una tabla (orden y ancho de columnas). */
export interface TableMeta {
  columnOrder?: string[];
  hidden?: string[];
  widths?: Record<string, number>;
  /** Colores asignados a cada opción de los campos `select`. */
  optionColors?: Record<string, Record<string, string>>;
  /**
   * Columnas del sistema (`id`, `created`, `updated`) que el constructor
   * pidió mostrar. Las que faltan quedan ocultas por defecto.
   */
  systemVisible?: string[];
  /** Filas por página en la cuadrícula. `0` significa mostrar todas. */
  pageSize?: number;
}

export interface TableRecord {
  id: string;
  app: string;
  name: string;
  label: string;
  dataCollection: string;
  order: number;
  fields: FieldDef[];
  meta: TableMeta;
  /**
   * Valores sin dueño que el constructor dio por buenos, por nombre de columna.
   * Dejan de contar en el recuento de filas sin enlace y siguen viéndose.
   *
   * Hace falta: sin esto, las cédulas de terceros que nunca van a tener registro
   * cuentan para siempre, el contador no llega a cero y se vuelve ruido que el
   * usuario aprende a ignorar.
   */
  acceptedOrphans?: Record<string, string[]>;
  /**
   * Tabla que sostiene la plataforma y no el constructor: no se puede borrar y
   * trae columnas del sistema. Hoy solo la de personas. Ver `shared/people.ts`.
   */
  system?: boolean;
}

/** Alguien con acceso a una aplicación publicada. */
export interface AppPerson {
  /**
   * Id de su cuenta en esta aplicación. Es con lo que llega la sesión, no con
   * lo que se enlaza: lo que guarda una columna que apunta a personas es
   * `fila`.
   */
  id: string;
  /**
   * Id de su fila en la tabla de personas de esta aplicación.
   *
   * Es el ancla de toda columna que apunte a la tabla de personas, igual que en
   * cualquier otra relación. Vacío solo mientras su fila todavía no existe
   * --alguien recién invitado, una aplicación a medio arrancar--; la repone
   * `syncPersonRows`.
   */
  fila?: string;
  name: string;
  email: string;
  /** Que pantallas ve. Puede tener varios de los roles de la app. */
  roles: string[];
  /**
   * Sus columnas propias en la tabla de personas de esta aplicación, por
   * nombre de columna. Vacío si la aplicación no le ha puesto ninguna.
   */
  campos?: Record<string, unknown>;
}

/**
 * La apariencia con la que se pinta la aplicación publicada: paleta y tamaño
 * de letra. Es la apariencia, no el modo: claro u oscuro lo elige quien mira
 * la página. Ver `shared/brand.ts`.
 */
export type { AppsLook, AppTheme };

/** El directorio de toda la instalación. Sin ajuste guardado, está desactivado. */
export interface DirectorySettings {
  enabled: boolean;
}

/** Solo la marca publicada, sin información del constructor ni del contenido. */
export interface DirectoryApp {
  id: string;
  slug: string;
  name: string;
  icon: string;
  visibility: AppRecord["visibility"];
  theme: AppTheme;
}

/** La sesión pertenece a esta aplicación; el correo no es una cuenta global. */
export interface DirectoryMemberApp extends DirectoryApp {
  token: string;
  member: { id: string; name: string; cuenta: string };
}

export interface DirectoryResponse {
  apps: DirectoryApp[];
}

export interface DirectorySession {
  apps: DirectoryMemberApp[];
}

/**
 * Lo que quien construye cuenta de su aplicación, con sus palabras: quién la
 * usa, para qué y cómo se habla en su trabajo, en un solo texto. Es opcional;
 * se sanea con `normalizeBrief()`. Lo lee la IA en cada petición y no viaja a
 * lo publicado.
 */
export interface AppBrief {
  descripcion?: string;
}

export interface AppRecord {
  id: string;
  name: string;
  slug: string;
  icon: string;
  visibility: "private" | "public";
  published: boolean;
  owner: string;
  /**
   * Otros usuarios del panel a los que un administrador le asignó la
   * aplicación. Entran como el dueño a todo, menos a borrarla.
   */
  editors?: string[];
  theme: AppTheme | null;
  /** Propósito y tono: para quién es y cómo se le habla. Vacío: sin decir. */
  brief?: AppBrief | null;
  /**
   * Roles que define esta aplicación, con el nombre que quiera el constructor.
   * Deciden que páginas y que bloques ve cada persona invitada.
   */
  roles: string[];
  /**
   * Id de la versión que ve el público. Vacío: la app todavía no se publicó
   * con el historial, y el enlace sirve el estado vivo.
   */
  liveVersion?: string;
  /**
   * Id de la conversación con la IA que quedó abierta, en la página que sea.
   * Vacío: ninguna, y cada página del panel empieza en blanco.
   */
  openChat?: string;
  created: string;
  updated: string;
}

/* ------------------------------------------------------------------ */
/* Usuarios del panel                                                   */
/* ------------------------------------------------------------------ */

/** Mínimo de caracteres de la clave de un usuario del panel (el de PocketBase). */
export const MIN_BUILDER_PASSWORD = 8;

/** Un usuario del panel, visto desde la gestión de usuarios. */
export interface BuilderAccount {
  id: string;
  email: string;
  name: string;
  /** Aplicaciones que creó: suyas, no asignadas. */
  owned: string[];
  /** Aplicaciones de otros que tiene asignadas. */
  assigned: string[];
}

/**
 * Lo que enseña la gestión de usuarios: todos menos la cuenta principal, y
 * todas las aplicaciones.
 */
export interface BuildersView {
  builders: BuilderAccount[];
  apps: Pick<AppRecord, "id" | "name" | "slug" | "icon" | "theme" | "owner">[];
}

/* ------------------------------------------------------------------ */
/* Tablas que declara una página                                        */
/* ------------------------------------------------------------------ */

/**
 * Define las operaciones de datos que viajan del documento al servidor. crear, actualizar
 * y borrar requieren sesión.
 *
 * contar devuelve el total sin enviar filas. listar está limitada, por lo que contar
 * evita usar el tamaño de la muestra como total de la tabla.
 */
export type AccessOp = "listar" | "contar" | "obtener" | "crear" | "actualizar" | "borrar";

/**
 * Una tabla que usa el HTML de una página, con nombres propios.
 *
 * Las columnas se guardan por su identidad interna, no por su nombre: así
 * renombrar una columna no obliga a tocar el HTML. El puente traduce en los
 * dos sentidos usando este mapa.
 */
export interface HtmlSource {
  /** Nombre con el que el HTML pide esta fuente. */
  name: string;
  tableId: string;
  /** Nombre logico de cada columna -> id interno de la columna. */
  fields: Record<string, string>;
}

export interface PageRecord {
  id: string;
  app: string;
  name: string;
  slug: string;
  icon: string;
  order: number;
  isHome: boolean;
  /** No es una página: es un texto que agrupa a las de alrededor en el sidebar. */
  separator?: boolean;
  /**
   * Huella del documento HTML de la página. Vacía: la página todavía no tiene
   * nada escrito.
   */
  doc?: string;
  /** Tablas que puede pedir el HTML de la página. Nada más está permitido. */
  sources?: HtmlSource[];
  /**
   * Roles que pueden abrirla. Vacío: la abre cualquiera que alcance la
   * aplicación --lo que la pantalla enseña como `Todos`--.
   *
   * Con uno o más roles marcados la página exige sesión iniciada y uno de
   * ellos, aunque la aplicación sea pública. No hay un estado guardado de
   * `Todos`: es la consecuencia de que la lista esté vacía. Ver `design.md` D2.
   */
  roles?: string[];
  /**
   * Las reglas funcionales de la página, en markdown plano y una por viñeta:
   * quien puede hacer que, que datos son obligatorios, que prohíbe el negocio.
   * Vacía: la página todavía no tiene ninguna regla guardada.
   *
   * No viaja a la aplicación publicada: es del constructor. Ver `page-memory`.
   */
  memory?: string;
  /**
   * Los conectores y operaciones que llama su HTML, leídos de él al guardarlo.
   * No da ni quita permiso: sirve para contar qué páginas usan un conector.
   */
  connectors?: PageConnectorUse[];
}

/** Un conector que llama el HTML de una página, con sus operaciones. */
export interface PageConnectorUse {
  /** El nombre del conector, tal como lo escribe el HTML. */
  connector: string;
  operations: string[];
}

/** Todo lo que necesita una app publicada para dibujarse. */
export interface AppBundle {
  app: Pick<AppRecord, "id" | "name" | "slug" | "icon" | "visibility" | "published" | "theme">;
  pages: PageRecord[];
  tables: TableRecord[];
  /**
   * Roles de quien está mirando. El HTML de una página no puede tener su
   * propio padrón de personas, así que necesita esto para saber a quien tiene
   * delante. En la vista previa son todos los de la app: el constructor lo ve
   * todo.
   */
  roles?: string[];
  /**
   * Hay páginas que quien mira sin sesión no ve porque se abren con un rol.
   * Solo viaja en una aplicación pública y sin sesión: es lo que hace aparecer
   * «Iniciar sesión», sin decir cuáles son ni cómo se llaman.
   */
  hiddenPages?: boolean;
}

/**
 * Código del error de intentar guardar sin cuenta iniciada.
 *
 * Viaja aparte del mensaje para que el puente lo distinga del resto de errores
 * y ponga el aviso de iniciar sesión sin que el HTML de la página participe.
 * Ver `design.md` D4.
 */
export const NEEDS_SESSION = "sin-sesion";

/**
 * Código del error de una página que pide una tabla o una columna que su
 * manifiesto no cubre: la fuente no está declarada, o su tabla ya no existe.
 *
 * Viaja aparte del mensaje para que el panel del constructor ofrezca
 * «Reparar conexión» sin leer el texto. Ver `resolveSource`.
 */
export const SOURCE_BROKEN = "fuente-rota";

export interface ApiError {
  error: string;
  /** Que clase de error es, cuando el mensaje no basta para distinguirlo. */
  codigo?: string;
}

/* ------------------------------------------------------------------ */
/* Historial de versiones                                               */
/* ------------------------------------------------------------------ */

/**
 * Una página tal como quedó guardada en una versión.
 *
 * El HTML no se copia: se nombra por la huella de su contenido, igual que en
 * el borrador.
 */
export type SnapshotPage = Pick<
  PageRecord,
  "id" | "name" | "slug" | "icon" | "order" | "isHome" | "separator" | "roles"
> & {
  doc?: string;
  sources?: HtmlSource[];
  connectors?: PageConnectorUse[];
};

/**
 * Presentación de una tabla dentro de una versión.
 * Nunca guarda columnas ni tipos: la estructura real vive en la base y se
 * resuelve al momento de servir, para que restaurar no pueda perder datos.
 */
export interface SnapshotTable {
  id: string;
  label: string;
  order: number;
  meta: TableMeta;
  /** Etiqueta visible de cada columna, por su id interno. */
  fieldLabels: Record<string, string>;
}

/** Todo el diseño de una aplicación en un momento dado. */
export interface DesignSnapshot {
  name: string;
  icon: string;
  theme: AppTheme | null;
  roles: string[];
  pages: SnapshotPage[];
  tables: SnapshotTable[];
}

/**
 * Como nació una versión: al publicar, guardada a mano (o antes de una
 * petición de la IA del panel), o por el trabajo de un asistente externo por
 * MCP, que deja una al empezar y otra al terminar. Es también la lista de
 * valores de la columna `kind` (`server/bootstrap.ts`).
 */
export const VERSION_KINDS = ["publish", "manual", "mcp"] as const;
export type VersionKind = (typeof VERSION_KINDS)[number];

/** Cuantas versiones sin fijar se conservan por aplicación; el resto se recorta. */
export const MAX_VERSIONS = 30;

export interface AppVersion {
  id: string;
  app: string;
  /** Número correlativo dentro de la app, para nombrarla sin ambigüedad. */
  number: number;
  label: string;
  kind: VersionKind;
  /** Huella del contenido; sirve para saber si el borrador cambió. */
  hash: string;
  /** Protegida del recorte automático. */
  pinned: boolean;
  author: string;
  snapshot: DesignSnapshot;
  created: string;
  updated: string;
}

/** Lo que se manda al panel: la lista sin el peso de cada fotografía. */
export type AppVersionSummary = Omit<AppVersion, "snapshot"> & {
  /** Nombre de quien la creó, ya resuelto. */
  authorName: string;
  live: boolean;
};

export interface VersionsView {
  versions: AppVersionSummary[];
  /** Huella del borrador actual. */
  draftHash: string;
  /** El borrador es distinto de la versión en vivo. */
  hasChanges: boolean;
  liveVersion: string;
}

/* ------------------------------------------------------------------ */
/* Servidor de inteligencia artificial                                  */
/* ------------------------------------------------------------------ */

/**
 * Que clase de servidor de IA es.
 *
 * Decide dos cosas: con que formato se le habla y de donde salen los datos de
 * sus modelos. `openrouter` habla el formato de ChatGPT, pero además tiene un
 * catálogo público, así que sus modelos no hay que describirlos a mano.
 */
export type AiProvider = "anthropic" | "openai" | "openrouter" | "llamacpp";

/**
 * Cuanto se le pide pensar antes de responder, con el nombre que le da su
 * propio servidor: `low`, `high`, `max`...
 *
 * No es una escala cerrada porque no lo es en ningún sitio: cada modelo dice
 * cuales ofrece --OpenRouter lo publica en su catálogo-- y ahí aparecen niveles
 * que la plataforma no puede conocer de antemano. Lo único fijo es `off`, que
 * significa no pedir nada y lo entienden todos porque no se manda.
 */
export type AiThinking = string;

/**
 * Lo más largo que puede ser una petición a la IA de una página.
 *
 * Vive aquí porque lo usan dos lados: el servidor la rechaza por encima
 * (`askPage` en `server/routes.ts`) y los atajos del panel
 * (`web/src/lib/quickAsk.ts`) mandan un texto ya redactado que tiene que
 * caber. Un atajo que se pasa no avisa al escribirlo: falla al pulsarlo.
 * `bun run harness` lo comprueba.
 */
export const MAX_AI_PROMPT = 4000;

/** No pensar. Es lo único que vale igual en todos los servidores. */
export const AI_THINKING_OFF = "off";

/**
 * Los niveles que se ofrecen cuando el servidor no dice cuales tiene. Es el
 * caso de Claude y de los compatibles con ChatGPT, que no publican catálogo.
 */
export const AI_THINKING_DEFAULT: AiThinking[] = ["off", "low", "medium", "high"];

/** Un modelo de los que ofrece un proveedor. */
export interface AiModel {
  /** El nombre con el que lo conoce su servidor: `claude-opus-5`, `gpt-5`. */
  id: string;
  /** Como se le llama en el panel. Vacío: se le llama por su nombre técnico. */
  label: string;
  /** Tope de lo que puede escribir en una respuesta. */
  maxTokens: number;
  /** Cuanto le cabe delante, petición y respuesta juntas. 0: no se sabe. */
  contextWindow: number;
  /** Si sabe pensar antes de responder. */
  thinking: boolean;
  /**
   * Los niveles de pensamiento que este modelo ofrece, tal y como los nombra
   * su servidor. Vacío: no los declara, y se ofrecen los de siempre.
   *
   * Se guardan con su nombre de verdad --y no traducidos a una escala
   * propia-- porque es lo que hay que mandarle luego: un modelo que ofrece
   * `max` no entiende otra cosa. Si "sin pensar" no está en la lista, es que
   * este modelo piensa siempre.
   */
  efforts: AiThinking[];
  /** Si sabe mirar imágenes. Solo se muestra; todavía no se le manda ninguna. */
  vision: boolean;
  /**
   * Si piensa se le pide con un tope de tokens, no con un nombre de nivel.
   * Es como lo hace la propia interfaz de llama.cpp: sirve en cualquier
   * modelo con bloque `<think>`, declare o no sus propios niveles. Con esto
   * encendido, `efforts` siempre trae los niveles fijos de llama.cpp.
   */
  thinkingBudget?: boolean;
}

/** Un servidor de IA conectado, con los modelos que se le van a pedir. */
export interface AiProviderConfig {
  /** Nombre interno, para poder renombrar el visible sin perder la elección. */
  id: string;
  /** Como se le llama en el panel: "Claude del trabajo", "Ollama de casa". */
  name: string;
  provider: AiProvider;
  /** Dirección del servidor. Vacío usa la oficial del proveedor. */
  baseUrl: string;
  models: AiModel[];
  /**
   * Si se puede usar. Apagarlo lo esconde del chat sin borrarlo ni perder su
   * clave: sirve para dejar preparado un servidor que hoy no se quiere pagar.
   */
  enabled: boolean;
}

/** Con que se atiende una petición: de que servidor, que modelo y cuanto piensa. */
export interface AiChoice {
  /** El `id` del proveedor. */
  provider: string;
  /** El `id` del modelo dentro de ese proveedor. */
  model: string;
  thinking: AiThinking;
}

/**
 * La intención de modo Plan que manda el botón del composer con la siguiente
 * petición. Quien decide el estado real es el servidor, a partir del hilo: ver
 * `design.md` D1 de `ia-modo-plan`.
 *
 * - `activar`: empezar --o seguir en-- modo Plan para esta petición.
 * - `cortar`: orden de implementar a mitad de conversación (D4): no manda
 *   texto nuevo al modelo, cierra el plan con lo que la IA tenía hasta ahora y
 *   lo deja listo para construir.
 * - `implementar`: pasar de un plan ya cerrado a modo Implementador.
 */
export type AiPlanIntent = "activar" | "cortar" | "implementar";

export interface AiConfig {
  providers: AiProviderConfig[];
  /** Lo que se usa cuando quien pide no elige otra cosa. */
  fallback: AiChoice;
  /**
   * Con que se atiende la pasada que escribe la memoria de una página.
   *
   * Vacío --lo normal-- es el modelo con el que se atendió la petición: una
   * segunda configuración que nadie mantiene se queda vieja. Está por si más
   * adelante conviene uno más barato para esta tarea, que es corta y siempre
   * la misma. Ver `memoria-de-pagina/design.md` D7.
   */
  memoryChoice: AiChoice | null;
  enabled: boolean;
  /** Muestra en cada respuesta del chat el contexto que se le manda al modelo. */
  debugButton: boolean;
  /**
   * Muestra en el chat los submenús de modelo y razonamiento. Apagado, cada
   * petición va con el modelo y el razonamiento por defecto (`fallback`).
   */
  modelPicker: boolean;
  /**
   * Minutos que se deja correr una petición antes de cortarla sola, como si
   * quien la pidió hubiera apretado "detener". Protege contra un proveedor
   * colgado o una conversación que no cierra sola. 0: sin tope.
   */
  runTimeoutMinutes: number;
}

/** Un proveedor tal y como se le muestra al panel: nunca incluye la clave. */
export interface AiProviderView extends AiProviderConfig {
  hasKey: boolean;
  /**
   * Los últimos caracteres de la clave guardada, para reconocer cuál es sin
   * enseñarla (`aiKeyHint()` en `server/routes.ts`). Vacío si no hay clave o es
   * demasiado corta para enseñar un trozo sin regalarla.
   */
  keyHint?: string;
}

/** Lo que se le muestra al panel: nunca incluye ninguna clave. */
export interface AiConfigView extends Omit<AiConfig, "providers"> {
  providers: AiProviderView[];
}

/**
 * Cuanto contexto lleva gastado la petición.
 *
 * `input` es todo lo que el modelo tuvo delante en el último turno --el
 * sistema, la conversación entera y lo que devolvieron las herramientas--, así
 * que es lo que de verdad ocupa. Contra `window` se ve cuanto queda libre.
 */
export interface AiUsage {
  input: number;
  output: number;
  /** Lo que le cabe al modelo. 0: no está declarado y no hay contra que medir. */
  window: number;
  /** El modelo que atendió, para poder decirlo aunque se cambie después. */
  model: string;
  /** De `input`, lo que el proveedor leyó de su caché, si lo informa. */
  cached?: number;
  /** Las sumas de todas las llamadas al modelo de esta petición. */
  totals?: AiUsageTotals;
  /**
   * Lo que ocupará, más o menos, la próxima petición de esta conversación antes
   * de escribir nada: las instrucciones, las herramientas y los turnos
   * anteriores que se arrastran. Suele ser menos que `input`, porque lo que
   * devolvieron las herramientas no pasa a la siguiente.
   */
  next?: AiNextRequest;
}

/** El reparto estimado de la próxima petición, en tokens. */
export interface AiNextRequest {
  system: number;
  tools: number;
  history: number;
  total: number;
}

/**
 * Lo gastado sumando llamadas, a diferencia de `AiUsage.input`, que es lo que
 * ocupa la última. Sumar entradas no dice cuánto contexto queda --cada llamada
 * reenvía la conversación entera--, pero sí cuánto se evaluó y cuánto se
 * escribió, y con los segundos, a qué velocidad.
 */
export interface AiUsageTotals {
  input: number;
  output: number;
  cached: number;
  /** Lo que tardaron las llamadas, para la velocidad media de escritura. */
  seconds: number;
}

/** Una acción que la IA realizo. */
export interface AiStep {
  tool: string;
  summary: string;
  ok: boolean;
}

/* ------------------------------------------------------------------ */
/* El recorrido de una petición                                         */
/* ------------------------------------------------------------------ */

/** Una herramienta que se usó en una ronda, con lo que tardó. */
export interface AiTraceTool {
  tool: string;
  /** Lo que dijo su paso, o el nombre de la herramienta si no dejó ninguno. */
  summary: string;
  ok: boolean;
  seconds: number;
  /** La pidió la plataforma y no el modelo: la revisión de cierre. */
  forced?: boolean;
}

/** Una llamada al modelo y lo que se hizo con su respuesta. */
export interface AiTraceRound {
  /** Lo que tardó el modelo en contestar, sin las herramientas. */
  seconds: number;
  input: number;
  output: number;
  cached: number;
  tools: AiTraceTool[];
  /**
   * Lo que se esperó a las herramientas de la ronda, de reloj. No es la suma
   * de `tools`: las lecturas corren a la vez.
   */
  toolsSeconds?: number;
  /**
   * Lo que la plataforma le dijo al modelo al cerrar la ronda en vez de darla
   * por buena (`revisar`, `guardado`, `vacia`, `plan`, `mensaje`, `pegada`, `idioma`).
   */
  nudge?: string;
}

/**
 * Cómo fue una petición por dentro, ronda a ronda. Es lo que dibuja la vista
 * de recorrido y de donde salen las estadísticas de uso.
 */
export interface AiTrace {
  provider: string;
  model: string;
  thinking: string;
  /** La versión de las instrucciones con que se atendió (`PROMPT_VERSION`). */
  promptVersion: string;
  /**
   * Cómo se leyó la petición antes de atenderla (`requestIntent()` en
   * `shared/requestIntent.ts`): una pregunta se responde sin construir y una
   * pantalla de datos sin tema se aclara primero; aceptar los registros de prueba
   * solo llena tablas. No va en el modo Plan.
   */
  intent?: "pregunta" | "abierta" | "datos" | "construir";
  rounds: AiTraceRound[];
}

/** Lo que quien construye opinó de una respuesta. */
export interface AiRating {
  value: "bien" | "mal";
  /** Lo que escribió para explicarlo, si escribió algo. */
  note?: string;
}

/* ------------------------------------------------------------------ */
/* Conversaciones con la inteligencia artificial                        */
/* ------------------------------------------------------------------ */

export interface AiMessage {
  notices?: string[];
  from: "yo" | "ia";
  text: string;
  /**
   * Lo que se lee en la burbuja cuando no es lo que se mandó: un atajo como
   * «Mejorar apariencia» manda un texto largo para el modelo y se lee corto.
   */
  label?: string;
  steps?: AiStep[];
  /**
   * La pregunta que la IA dejó abierta en este mensaje, si preguntó. Se guarda
   * para que al volver a la conversación se lea que se preguntó, no solo la
   * respuesta que vino después.
   */
  question?: AiQuestion;
  /**
   * El plan con el que este mensaje cerró el modo Plan, si lo cerró. Mismo
   * patrón que `question`: un campo opcional que distingue este turno de una
   * respuesta normal. `implementado` empieza en falso y pasa a verdadero
   * cuando el servidor confirma que la ejecución terminó; desde entonces el
   * plan sigue visible pero no se edita ni se reabre. Ver `design.md` D2 de
   * `ia-modo-plan`.
   */
  plan?: { texto: string; implementado: boolean; estado?: "implementando" | "incompleto" };
  /**
   * Este turno se atendió en modo Plan: la IA conversaba para planear, no
   * construia. Es lo que deja ofrecer "Implementar ahora" al pie de su
   * respuesta; una respuesta normal, aunque después se encienda el modo, no
   * tiene ningún plan que cortar.
   */
  planning?: boolean;
  /**
   * El razonamiento que el modelo dejó escrito antes de responder, si el
   * servidor de IA lo envió. No siempre existe: depende del modelo.
   */
  reasoning?: string;
  /**
   * Los archivos que se adjuntaron a la petición: su nombre y la referencia de
   * lo guardado. El contenido no se guarda aquí --vive en el almacén de
   * adjuntos-- pero la referencia si, y es lo que hace que la IA los siga
   * teniendo delante en los turnos siguientes.
   */
  files?: AiChatFile[];
  /**
   * Cómo se llamaba cada elemento señalado con el cursor (`PickedBlock.label`).
   * El HTML del elemento no se guarda, por lo mismo que el de los archivos.
   */
  picked?: string[];
  /** Los conectores que se adjuntaron a la petición desde el chat, por su nombre. */
  connectors?: string[];
  /**
   * Lo que gastó la petición que cerró este mensaje (solo en los de la IA). Se
   * guarda para que el medidor de contexto siga ahí al volver a la
   * conversación, también después de recargar.
   */
  usage?: AiUsage;
  /**
   * Cuánto tardó la petición que cerró este mensaje, en segundos de reloj: desde
   * que se pidió hasta que terminó, con las herramientas incluidas (solo en los
   * de la IA).
   */
  elapsed?: number;
  /** El recorrido de la petición, ronda a ronda (solo en los de la IA). */
  trace?: AiTrace;
  /**
   * La fila de `ai_requests` de esta petición (solo en los de la IA): a donde
   * va la valoración para que cuente en las estadísticas.
   */
  requestId?: string;
  /** Lo que quien construye opinó de esta respuesta. */
  rating?: AiRating;
  /**
   * La petición quedó a medias --se acabaron los pasos, o se reinició el
   * servidor-- y se puede seguir desde donde quedó con «Continuar».
   */
  continuable?: boolean;
  /**
   * Se escribió mientras la IA trabajaba y le llegó entre una ronda y la
   * siguiente (solo en los de quien pide).
   */
  steering?: boolean;
}

/**
 * Una conversación pertenece a la página donde se hizo: la lista de una página
 * enseña solo las suyas, y no se puede abrir una hecha en otra. Borrar la
 * página se lleva sus conversaciones.
 */
export interface AiChat {
  id: string;
  app: string;
  /**
   * Id de la página donde se abrio. En una del modo construcción, la
   * primera página que creó la IA; vacío mientras no haya creado ninguna.
   */
  page: string;
  /**
   * `construir`: se abrio desde el modo construcción y no es de una
   * página sino de la aplicación entera. Se sigue desde allí aunque quede
   * guardada en la primera página que creó.
   */
  mode?: AiChatMode;
  /** Lo primero que se pidió, recortado; después, el que propone la IA. */
  title: string;
  messages: AiMessage[];
  /**
   * El resumen de los turnos más viejos, que ya no caben enteros delante del
   * modelo. `upTo` dice cuántos mensajes del principio cubre: se rehace solo
   * cuando quedan fuera más de los que ya resume.
   */
  summary?: { upTo: number; text: string };
  created: string;
  updated: string;
}

/** De que es una conversación. Sin modo, de la página donde se abrio. */
export type AiChatMode = "construir";

/**
 * La página que no existe: donde vive la conversación del modo
 * construcción, que no es de ninguna página. Ocupa el sitio del id de página
 * en las rutas de la IA, en la clave de la conversación y en la dirección del
 * constructor. Un id de PocketBase tiene 15 caracteres, así que no choca.
 */
export const BUILD_MODE = "construir";

/** La lista de conversaciones: sin el peso de los mensajes. */
export type AiChatSummary = Omit<AiChat, "messages"> & { count: number };

/**
 * La conversación que quedó abierta, que es una sola en toda la aplicación: la
 * última en la que se habló, en cualquiera de sus páginas.
 *
 * Solo hace falta saber cual es y de que página: con eso, la página que la
 * tiene la repone al abrirse y las demás empiezan en blanco.
 */
export interface AiOpenChat {
  /** Id de la conversación. */
  chat: string;
  /**
   * Id de la página donde se tuvo, o `BUILD_MODE` si es del modo
   * construcción: esa se repone allí, no en la página donde quedó guardada.
   */
  page: string;
}

/* ------------------------------------------------------------------ */
/* Impacto de un cambio en la base de datos                             */
/* ------------------------------------------------------------------ */

/**
 * Los cambios de estructura con riesgo. Los que no lo tienen --crear una
 * tabla, añadir una columna, renombrar una columna-- se aplican solos y no
 * llegan hasta aquí.
 */
export type ChangeKind = "borrar_columna" | "cambiar_tipo" | "borrar_tabla";

export interface StructureChange {
  kind: ChangeKind;
  tableId: string;
  tableLabel: string;
  /** Nombre técnico de la columna, cuando el cambio es de columna. */
  field?: string;
  fieldLabel?: string;
  /** Id interno de la columna: es lo que declaran las páginas. */
  fieldId?: string;
  /** Tipo pedido, solo para el cambio de tipo. */
  newType?: FieldType;
  /** El cambio contado en una línea. */
  what: string;
}

/** Una página que declara lo que se va a cambiar. */
export interface ImpactedPage {
  id: string;
  name: string;
}

/**
 * `conservar`: se hace la variante que no destruye nada.
 * `aplicar`: se hace el cambio y las páginas afectadas quedan como están.
 * `aplicar_y_arreglar`: además la IA reescribe esas páginas.
 * `no_tocar`: la tabla se queda igual.
 */
export type ImpactChoice = "conservar" | "aplicar" | "aplicar_y_arreglar" | "no_tocar";

export interface ImpactOption {
  id: ImpactChoice;
  label: string;
  hint: string;
}

export interface DataImpact {
  /** La petición que motiva el cambio: el "para que" del diálogo. */
  request: string;
  changes: StructureChange[];
  pages: ImpactedPage[];
  /** La primera es siempre la que no rompe nada. */
  options: ImpactOption[];
}

/* ------------------------------------------------------------------ */
/* Cambios de acceso                                                    */
/* ------------------------------------------------------------------ */

/**
 * Hacia donde va un cambio de acceso.
 *
 * Es lo único que decide cuanta fricción lleva. Dar acceso nuevo no se puede
 * deshacer --lo que alguien vio, lo vio-- y por eso siempre pasa por una
 * confirmación que nombra la consecuencia. Quitarlo se resuelve directo:
 * como mucho corrige algo que sobraba, y el punto de vuelta atrás sigue ahí.
 */
export type AccessDirection = "dar" | "quitar";

/**
 * Un cambio de roles sobre una persona invitada, pedido por la IA.
 *
 * No es un `StructureChange`: no tiene tabla, y las cuatro salidas del diálogo
 * de impacto --conservar, aplicar, aplicar y arreglar, no tocar-- no
 * significan nada para un permiso. Va por su lado, con su aviso propio.
 */
export interface AccessChange {
  /** Id de la cuenta de la persona dentro de esta aplicación. */
  personId: string;
  /** Como se le llama en el aviso. */
  personName: string;
  personEmail: string;
  direction: AccessDirection;
  /** Los roles con los que queda. Es la lista entera, no lo que se añade. */
  roles: string[];
  /**
   * Lo que pasa a poder hacer --o a no poder hacer-- esa persona, dicho en una
   * frase y en lenguaje llano: "Ana podrá ver los pedidos de todos los
   * clientes, no solo los suyos". No un botón genérico de autorizar.
   */
  consequence: string;
}

/**
 * Algo que la IA pidió y que no se aplica hasta que quien construye lo
 * confirma desde la conversación: borrar una página o un separador, quitarle
 * el acceso a personas o quitar roles de la aplicación.
 *
 * Como `AccessChange`, va aparte del diálogo de impacto: no cambia una tabla y
 * sus salidas no significan nada aquí. La consecuencia está escrita para quien
 * construye y es lo que se lee antes de confirmar; el servidor vuelve a
 * comprobarlo todo al aplicarlo (`resolveAction` en `server/routes.ts`).
 */
export type AiPendingAction =
  | {
      kind: "borrar_pagina";
      pageId: string;
      pageName: string;
      /** Es un separador: se va el título y sus páginas se quedan donde están. */
      separator: boolean;
      consequence: string;
    }
  | {
      kind: "quitar_acceso";
      /** Las cuentas de esta aplicación que dejan de entrar. */
      people: { id: string; name: string; email: string }[];
      consequence: string;
    }
  | {
      kind: "quitar_roles";
      roles: string[];
      consequence: string;
    }
  | {
      /** Filas de una tabla de datos, por id. Se borran de verdad: las versiones no guardan datos. */
      kind: "borrar_filas";
      /** El nombre técnico de la tabla, el que se vuelve a buscar al confirmar. */
      table: string;
      tableLabel: string;
      ids: string[];
      consequence: string;
    }
  | {
      /**
       * Envíos a una operación de escritura de un conector. La IA nunca la
       * llama por su cuenta: los propone todos juntos en una tarjeta y solo
       * se envían si quien construye aprueba.
       */
      kind: "enviar_conector";
      connectorId: string;
      connectorName: string;
      operation: string;
      operationDescription: string;
      /** Los parámetros de cada envío, completos. */
      sends: Record<string, unknown>[];
      /** El texto del botón que envía: «Crear 3 contactos de prueba». */
      label: string;
      /** El conector estaba en depuración: los envíos se registran y no salen. */
      debug: boolean;
      consequence: string;
    };

/* ------------------------------------------------------------------ */
/* Lo señalado con el cursor                                            */
/* ------------------------------------------------------------------ */

/**
 * Un elemento del documento señalado con el cursor de selección.
 *
 * Nace en el puente --que es quien puede escuchar dentro del marco-- viaja al
 * panel como badge de la conversación, y de ahí al servidor con la petición.
 *
 * Lleva donde está y el HTML que tiene ahora, para que la IA no necesite
 * releer la página entera para saber que se le senalo. Si el elemento es
 * enorme, el HTML va recortado y se dice.
 */
export interface PickedBlock {
  /** Identidad del badge en el panel. No significa nada fuera de el. */
  id: string;
  /** El nombre de bloque que ya lleva, si alguna edición se lo puso. */
  name: string;
  /** Como se le llama en la conversación: "tabla Clientes", "sección Precios". */
  label: string;
  /** La etiqueta HTML que es, en minuscula: "h1", "div", "span". */
  tag: string;
  /** Donde está dentro del documento, en forma de selector. */
  path: string;
  /** El HTML que tiene ahora. */
  html: string;
  /** El HTML va recortado: no es todo lo que hay dentro. */
  truncated: boolean;
}

/* ------------------------------------------------------------------ */
/* Archivos como contexto de la IA                                      */
/* ------------------------------------------------------------------ */

/**
 * De que clase es un archivo que se le adjunta a la IA.
 *
 * No es su extensión ni su MIME: es como hay que contarselo al modelo. Todo lo
 * que se lee como texto viaja dentro del contexto, en un bloque de código con
 * su lenguaje puesto; una imagen no, y por eso es la única clase aparte.
 *
 * Una hoja de cálculo entra como `sheet` aunque llegue en `.xlsx`: lo que
 * viaja es su contenido convertido a CSV, que es lo que un modelo lee bien.
 */
export type AiFileKind = "html" | "css" | "js" | "json" | "csv" | "sheet" | "text" | "image";

/**
 * Un archivo adjunto a una petición a la IA.
 *
 * Nace en el navegador --se suelta encima del editor-- se sube al almacén de
 * adjuntos en cuanto se suelta, y vive como badge en la conversación mientras
 * se escribe la petición.
 *
 * Lleva la referencia de lo guardado, no el contenido: el contenido se guarda
 * una sola vez (`server/ai/aiFiles.ts`) y de ahí lo leen tanto la muestra que se
 * le cuenta al modelo como las órdenes que lo abren entero. Así un archivo de
 * varios megabytes no engorda ninguna petición, y sigue estando en los turnos
 * siguientes de la conversación.
 */
export interface AiFile {
  /** Identidad del badge en el panel. No significa nada fuera de el. */
  id: string;
  /**
   * El adjunto guardado. Vacío mientras la subida va en camino: un badge sin
   * referencia todavía no se puede mandar con una petición.
   */
  ref: string;
  /** El nombre del archivo, tal como venía. */
  name: string;
  kind: AiFileKind;
  /**
   * El tipo que declaró el navegador, o el que tiene ya guardado: una imagen
   * se guarda en WebP. Puede venir vacío.
   */
  mime: string;
  /** Lo que ocupa, en bytes: el original mientras sube, lo guardado después. */
  size: number;
}

/**
 * Un adjunto tal y como queda nombrado dentro de una conversación guardada.
 *
 * Es lo que hace que preguntar por un archivo dos turnos después no obligue a
 * adjuntarlo otra vez: la conversación recuerda que adjuntos nombró cada
 * petición, y el contexto de la siguiente los vuelve a poner delante.
 *
 * También es lo que decide cuanto vive un adjunto: uno que ninguna conversación
 * nombra se puede borrar.
 */
/**
 * Cuantos archivos --imágenes incluidas, cuentan juntos-- caben en una misma
 * petición. Lo usan el panel, que avisa, y el servidor, que recorta.
 */
export const MAX_AI_FILES = 8;

/** Cuantos elementos señalados con el cursor caben en una misma petición. */
export const MAX_AI_PICKED = 8;

export interface AiChatFile {
  /** El adjunto guardado. */
  ref: string;
  name: string;
  kind: AiFileKind;
  /** Lo que ocupa, en bytes. Se lee en la burbuja de la petición. */
  size: number;
}

/* ------------------------------------------------------------------ */
/* Lo que la IA pregunta antes de construir                             */
/* ------------------------------------------------------------------ */

/** Una de las salidas de una pregunta: lo que se lee en el botón. */
export interface AiQuestionOption {
  /** El texto del botón. Corto: es lo que se elige de un vistazo. */
  label: string;
  /** Que significa elegirlo, si el rótulo solo no basta. */
  description?: string;
}

/**
 * Lo que la IA pregunta cuando no puede seguir sin saberlo.
 *
 * Solo cabe aquí una elección entre cosas que ya existen en la aplicación
 * --que tabla, que fuente--: una pregunta de diseño abierto no tiene opciones
 * que ofrecer y se resuelve construyendo algo y dejando corregir.
 *
 * Preguntar cierra el turno: la IA no sigue escribiendo después de preguntar.
 * Elegir una opción manda una petición nueva.
 */
export interface AiQuestion {
  /** Lo que se pregunta, en una línea. */
  question: string;
  /** De que va, en dos o tres palabras. Rotula el bloque en la conversación. */
  header: string;
  options: AiQuestionOption[];
}

/** Lo que responde la IA cuando escribe una página. */
export interface AiPageResult {
  /** El turno terminó sin errores ni decisiones pendientes. */
  completed?: boolean;
  implementation?: "implementado" | "incompleto";
  message: string;
  steps: AiStep[];
  /** Avisos cortos de lo que se aplicó solo por no tener riesgo. */
  notices: string[];
  /** La página cambió: hay que volver a leerla. */
  changed: boolean;
  /** Cambios con riesgo esperando decisión. Vacío: no hay nada que preguntar. */
  impact: DataImpact | null;
  /**
   * La IA cerró el turno preguntando en vez de construir. `null` es lo normal:
   * preguntar está acotado a elegir entre cosas que ya existen.
   */
  question: AiQuestion | null;
  /**
   * El plan con el que se cerró el turno, si se cerró uno. `null` es lo
   * normal, igual que `question`.
   */
  plan: { texto: string; implementado: boolean; estado?: "implementando" | "incompleto" } | null;
  /** Se atendió en modo Plan. Ver `AiMessage.planning`. */
  planning?: boolean;
  /**
   * Accesos que la IA quiere dar y todavía no ha dado: cada uno se confirma
   * por separado, con su consecuencia delante. Los que quitan no llegan aquí,
   * se aplican solos.
   */
  access: AccessChange[];
  /**
   * Lo que la IA pidió borrar o quitar y espera confirmación: cada uno se
   * confirma por separado, con su consecuencia delante. Ver `AiPendingAction`.
   */
  actions?: AiPendingAction[];
  /** Conversación donde quedó guardada la petición. */
  chatId: string;
  /** El razonamiento del modelo, si lo envió. */
  reasoning?: string;
  /** Se detuvo a media petición porque se pidió pararla. */
  stopped: boolean;
  /** Cuanto contexto se gastó, si el servidor lo contó. */
  usage?: AiUsage;
  /** Cuánto tardó la petición entera, en segundos. Ver `AiMessage.elapsed`. */
  elapsed?: number;
  /** Ver `AiMessage.trace`. */
  trace?: AiTrace;
  /** Ver `AiMessage.requestId`. */
  requestId?: string;
  /** Ver `AiMessage.continuable`. */
  continuable?: boolean;
  /**
   * La pasada de memoria todavía corre: su resultado llega después, en un
   * aviso `memoria`, sin hacer esperar a la respuesta.
   */
  memoryPending?: boolean;
  /** Los mensajes que llegaron mientras trabajaba, en el orden en que entraron. */
  steering?: string[];
}

/* ------------------------------------------------------------------ */
/* Estadísticas de uso de la IA                                         */
/* ------------------------------------------------------------------ */

/** Cómo le fue a un modelo en el periodo mirado. */
export interface AiStatsModel {
  model: string;
  provider: string;
  requests: number;
  /** Las valoradas como buenas y como malas. */
  good: number;
  bad: number;
  /** Las que terminaron sin pasos fallidos ni decisiones pendientes. */
  completed: number;
  /** Segundos de reloj, sumados: la media es `seconds / requests`. */
  seconds: number;
  rounds: number;
  input: number;
  output: number;
  cached: number;
  failedSteps: number;
}

/** Las valoraciones de una versión de las instrucciones con un modelo. */
export interface AiStatsVersion {
  version: string;
  model: string;
  requests: number;
  good: number;
  bad: number;
}

/** Una valoración con nota, para leerla tal cual. */
export interface AiStatsNote {
  id: string;
  value: "bien" | "mal";
  note: string;
  model: string;
  version: string;
  /** Lo que se pidió, recortado. */
  prompt: string;
  app: string;
  created: string;
}

/** Lo que enseña la pestaña «Uso de IA» de los ajustes. */
export interface AiStats {
  /** Desde cuándo se cuenta; vacío: desde siempre. */
  since: string;
  requests: number;
  /** Cuántas hay en cada estado: terminada, detenida, fallida, interrumpida, en_curso. */
  byStatus: Record<string, number>;
  byModel: AiStatsModel[];
  byVersion: AiStatsVersion[];
  /** Peticiones por día y modelo, del más viejo al más nuevo. */
  byDay: { day: string; models: Record<string, number> }[];
  notes: AiStatsNote[];
}

/** Cada cuánto se agrupa la línea de uso de la IA. */
export type AiTimelineUnit = "semana" | "mes" | "año";

/**
 * Peticiones a la IA a lo largo del tiempo, para la línea de «Uso de IA».
 *
 * No depende del periodo elegido arriba: una línea por meses en los últimos
 * 7 días sería un solo punto. Cada unidad trae su propio tramo --26 semanas,
 * 24 meses, todos los años-- y los huecos van con cero, no se saltan.
 */
export interface AiTimeline {
  unit: AiTimelineUnit;
  points: {
    /** Inicio del tramo: `AAAA-MM-DD` (el lunes) para semanas, `AAAA-MM` para meses, `AAAA` para años. */
    start: string;
    total: number;
    /** Peticiones de cada modelo en el tramo; las que no llegaron a un modelo solo cuentan en `total`. */
    models: Record<string, number>;
  }[];
}

/**
 * Una petición a la IA que el servidor tiene en marcha.
 *
 * La petición no vive en la conexión que la pidió: recargar o cerrar el
 * navegador no la corta. Esto es lo que hace falta para reconocerla al volver.
 */
export interface AiRunInfo {
  id: string;
  /** Lo que se pidió, para poder reponerlo en la conversación al volver. */
  prompt: string;
  /** Como se lee lo pedido, si fue un atajo con rótulo corto. */
  label?: string;
  /** Cuando empezó. */
  started: string;
  /** Ya terminó y solo está esperando a que alguien recoja el resultado. */
  done: boolean;
}

/**
 * Las páginas de una aplicación donde la IA está trabajando ahora mismo.
 *
 * Es la fuente de verdad de la señal: se pregunta al entrar en la aplicación,
 * y a partir de ahí la mantiene al día el hilo de avisos de cada petición. Una
 * petición terminada que solo espera a que recojan su resultado no está aquí:
 * no es trabajo en curso.
 */
export interface AiActiveRun {
  /**
   * Donde se pidió: la página de la conversación, o `BUILD_MODE` si se pidió
   * desde el modo construcción. Es donde vive el chat de la petición.
   * Vacío en un turno MCP: trabaja en toda la aplicación.
   */
  page: string;
  /**
   * Las otras páginas que la petición creó o está escribiendo. También
   * llevan la señal: quien llega a una de ellas tiene que saber que la IA
   * trabaja ahí, aunque la conversación esté en otro sitio.
   */
  pages?: string[];
  /** Cuando empezó. */
  started: string;
  /** `mcp` cuando es un asistente externo en modo MCP; sin él, la IA del panel. */
  kind?: "ia" | "mcp";
  /** Quién abrió el modo MCP: el nombre o el correo del constructor del token. */
  by?: string;
}

/* ------------------------------------------------------------------ */
/* Conexión MCP                                                         */
/* ------------------------------------------------------------------ */

/**
 * Las áreas sobre las que se dan permisos a un token MCP. `personas` es quién
 * entra a la aplicación publicada: invitar, editar y quitar personas, y crear
 * o quitar los roles de la aplicación.
 */
export const MCP_AREAS = ["paginas", "tablas", "datos", "personas"] as const;
export type McpArea = (typeof MCP_AREAS)[number];

/** Las cuatro acciones de cada área. */
export const MCP_ACTIONS = ["ver", "crear", "editar", "borrar"] as const;
export type McpAction = (typeof MCP_ACTIONS)[number];

/** Lo que puede hacer un token: área × acción. */
export type McpPerms = Record<McpArea, Record<McpAction, boolean>>;

/**
 * Los permisos con los que nace un token: ver, crear y editar páginas y
 * tablas; los datos solo se ven; nada se borra, y las personas ni se ven
 * --quien entra a la aplicación se abre a propósito--. Un token guardado antes
 * de que existiera un área la lee de aquí, así que las personas le quedan
 * cerradas.
 */
export const DEFAULT_MCP_PERMS: McpPerms = {
  paginas: { ver: true, crear: true, editar: true, borrar: false },
  tablas: { ver: true, crear: true, editar: true, borrar: false },
  datos: { ver: true, crear: false, editar: false, borrar: false },
  personas: { ver: false, crear: false, editar: false, borrar: false },
};

/** Lee unos permisos guardados o recibidos: lo que falte o no sea `true` es no. */
export function normalizeMcpPerms(raw: unknown): McpPerms {
  const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
  const out = {} as McpPerms;
  for (const area of MCP_AREAS) {
    const row =
      source && source[area] && typeof source[area] === "object"
        ? (source[area] as Record<string, unknown>)
        : null;
    out[area] = {} as Record<McpAction, boolean>;
    for (const action of MCP_ACTIONS) {
      out[area][action] = row ? row[action] === true : DEFAULT_MCP_PERMS[area][action];
    }
  }
  return out;
}

/** Lo que el panel sabe del token MCP de quien mira. Nunca el token. */
export interface McpTokenInfo {
  /** Tiene un token activo para esta aplicación. */
  active: boolean;
  perms: McpPerms;
  /** Cuándo se generó. */
  created?: string;
  /** La última vez que un asistente lo usó, con precisión de un minuto. */
  lastUsed?: string;
  /**
   * El token se puede volver a ver confirmando la contraseña. Los generados
   * antes de que se guardara no: funcionan, pero para verlo hay que generar
   * otro.
   */
  revealable: boolean;
  /** La dirección MCP de la aplicación, relativa al servidor (`/mcp/<id>`). */
  path: string;
}

/**
 * El token con su estado: lo único que lo lleva. Llega al generarlo y al
 * volver a verlo, y las dos cosas piden antes la contraseña de quien mira.
 */
export interface McpTokenSecret {
  token: string;
  info: McpTokenInfo;
}

/** Un turno MCP abierto, como lo ve el panel. */
export interface McpTurnInfo {
  /** Quién lo abrió. */
  by: string;
  started: string;
}

/** Por qué terminó un turno MCP. */
export type McpTurnEnd = "terminado" | "inactividad" | "interrumpido" | "reinicio";

/**
 * Lo que el panel recibe por `/api/apps/:id/mcp/seguir`, el hilo con el que
 * se entera del modo MCP sin preguntar.
 */
export type McpEvent =
  /** Cómo está ahora: lo primero que llega, y cada vez que cambia. */
  | { tipo: "turno"; turno: McpTurnInfo | null }
  /** El asistente cambió páginas o tablas: el panel vuelve a leerlas. */
  | { tipo: "cambio" }
  /** El asistente escribió una página y quiere probarla en el navegador. */
  | { tipo: "probar"; probeId: string; hash: string; page: string; fuentes?: HtmlSource[] }
  /** El turno terminó. */
  | { tipo: "cierre"; motivo: McpTurnEnd; resumen?: string };

/* ------------------------------------------------------------------ */
/* Registro de depuración de la IA                                      */
/* ------------------------------------------------------------------ */

/**
 * La constancia de la última petición de una página.
 *
 * Una por página, sobrescrita en cada petición: no es un historial. Existe
 * para poder entender después un fallo que no se puede reproducir, así que se
 * guarda siempre, haya terminado bien o mal, y sin depender de haber encendido
 * nada antes.
 */
export interface AiDebugEntry {
  id: string;
  app: string;
  page: string;
  /** Lo que se pidió. */
  prompt: string;
  /** El contexto que se le mandó al modelo, tal cual. */
  context: string;
  /** El razonamiento que dejó escrito, si lo envió. */
  reasoning: string;
  /** Lo que respondió, o el fallo si falló. */
  answer: string;
  /** El modelo que atendió. */
  model: string;
  /** Cuanto tardó, en milisegundos. */
  ms: number;
  /** El contexto no cabía entero y va recortado. */
  truncated: boolean;
  created: string;
  updated: string;
}

/** Lo que se lee del registro de una página. `null`: no hay nada guardado. */
export type AiDebugRead = AiDebugEntry | null;

/**
 * Lo que el servidor va contando mientras la IA trabaja.
 *
 * Es para mirar, no para decidir: quien construye puede desplegarlo y ver por
 * donde va, pero nada de esto es el resultado. El resultado llega una sola vez,
 * en `fin`, y es el mismo `AiPageResult` de siempre.
 *
 * Lo mismo se cuenta a quien pidió la petición y a quien se engancha después de
 * recargar: el que llega tarde recibe primero, de golpe, lo que ya había.
 */
export type AiProgress =
  /** Lo primero de toda conexión: a que petición se acaba de enganchar. */
  | { tipo: "inicio"; runId: string }
  | { tipo: "texto"; texto: string }
  | { tipo: "paso"; paso: AiStep }
  | { tipo: "razonamiento"; texto: string }
  /**
   * Lo que el modelo lleva escrito de la página entera, mientras la escribe.
   * Sin sus scripts y con las referencias de Planer puestas: es para verla
   * crecer en la vista previa, no para guardarla.
   */
  | {
      tipo: "borrador";
      html: string;
      /**
       * La página que se está escribiendo, si no es la de la conversación: el
       * borrador se dibuja en ella, no en la que se tiene delante.
       */
      pagina?: string;
    }
  /** Lo que se le mando al modelo en esta ronda. Solo llega en modo debug. */
  | { tipo: "contexto"; texto: string }
  /** Cuanto contexto lleva gastado. Llega al cerrar cada turno del modelo. */
  | { tipo: "uso"; uso: AiUsage }
  /**
   * La IA quiere ver si el HTML que acaba de escribir se dibuja sin errores.
   *
   * Esto no es para mirar, es lo unico que pide algo de vuelta: quien lo reciba
   * dibuja ese documento en un marco escondido y responde con lo que la consola
   * haya soltado. Si nadie lo atiende, la IA sigue sin saberlo.
   */
  | {
      tipo: "probar";
      probeId: string;
      hash: string;
      /**
       * Las fuentes que el documento declara ahora mismo. Van con la prueba
       * porque el panel recarga la página después de cada paso, y una prueba
       * pedida en el mismo paso que escribió el documento vería las de antes.
       */
      fuentes?: HtmlSource[];
      /**
       * La página cuyo documento se prueba, si no es la de la conversación. La
       * prueba lee sus datos con el permiso de esa página, y una conversación
       * del modo construcción no tiene página propia.
       */
      pagina?: string;
    }
  /**
   * Las páginas, además de la de la conversación, que la petición creó o
   * está escribiendo. Llega entera cada vez que crece: quien se engancha tarde
   * la recibe una sola vez con todas.
   */
  | { tipo: "paginas"; ids: string[] }
  /**
   * La IA pregunto y con eso cerro el turno. Llega antes del `fin` para que el
   * panel pueda pintarla en cuanto se sabe, sin esperar al resultado.
   */
  | { tipo: "pregunta"; pregunta: AiQuestion }
  /**
   * El modo Plan se cerro con este plan. Llega antes del `fin`, por lo mismo
   * que "pregunta": para que la tarjeta se pinte en cuanto se sabe.
   */
  | {
      tipo: "plan";
      plan: { texto: string; implementado: boolean; estado?: "implementando" | "incompleto" };
    }
  /** Un mensaje escrito mientras trabajaba, que el modelo ya recibió. */
  | { tipo: "mensaje"; texto: string }
  | { tipo: "fin"; resultado: AiPageResult }
  /**
   * La pasada de memoria terminó, después del `fin`. Trae los avisos que deja
   * (reglas actualizadas, o que no se pudieron guardar).
   */
  | { tipo: "memoria"; avisos: string[] }
  | { tipo: "error"; mensaje: string };

/**
 * Un fallo que soltó un documento al dibujarse.
 *
 * Es lo que se vería en la consola del navegador al inspeccionar la página,
 * recogido por el puente y traído hasta aquí.
 */
export interface PageIssue {
  /**
   * `js`: una excepción sin recoger. `promesa`: una promesa rechazada que nadie
   * atrapo. `recurso`: algo que la página pidió y no cargó. `consola`: lo que el
   * propio documento escribió con `console.error`. `datos`: los datos no
   * llegaron a la pantalla (los pone el servidor al leer `lecturas` y `vista`).
   * `espaciado`: cajas pegadas entre sí o texto contra el borde, medido sobre
   * lo dibujado (también lo pone el servidor, a partir de `vista`).
   */
  tipo: "js" | "promesa" | "recurso" | "consola" | "datos" | "espaciado";
  mensaje: string;
  linea?: number;
  columna?: number;
  pila?: string;
}

/** Lo que el panel devuelve cuando termina de probar un documento. */
export interface PageProbeReport {
  probeId: string;
  issues: PageIssue[];
  /** Avisos de `console.warn`. Van aparte: casi nunca son un fallo. */
  warnings: string[];
  /**
   * Si el puente del marco llegó a decir que estaba listo. Sin eso no corrió
   * nada de la página, y "no leyó nada" no se puede reprochar.
   */
  listo?: boolean;
  /** Lo que la página leyó de sus tablas mientras se probaba. */
  lecturas?: ProbeRead[];
  /** Lo que quedó en pantalla al terminar la prueba. */
  vista?: ProbeView;
}

/**
 * Una lectura de datos que hizo la página durante la prueba.
 *
 * Sin esto la prueba solo sabía de la consola, y una página que lee bien y
 * luego lo descarta todo --un filtro de fechas que deja fuera cada fila-- pasaba
 * por limpia mientras enseñaba ceros.
 */
export interface ProbeRead {
  op: "listar" | "contar" | "obtener";
  tabla: string;
  /** Filas que llegaron (en `contar`, ninguna). */
  filas: number;
  /** Cuantas cumplian lo pedido, según el servidor. */
  total: number;
  /** Si pidió con `filtro` o `buscar`: un cero entonces puede ser el filtro. */
  filtrada: boolean;
}

/** Un vistazo a lo que la página enseña, tomado dentro del marco. */
export interface ProbeView {
  /** Filas con datos de cada `<table>` (sin contar la de "no hay nada"). */
  tablas: number[];
  /** Por cada gráfica: cuantos puntos tiene y si alguno no es cero. */
  graficas: { puntos: number; conValor: boolean }[];
  /** Esqueletos de carga que siguen a la vista. */
  esqueletos: number;
  /**
   * Lienzos a la vista sin ninguna gráfica dentro, por su `id` (o "sin id"):
   * el hueco que dejó una gráfica que el código nunca llegó a pintar.
   */
  lienzos?: string[];
  /** Textos de avisos y estados vacíos a la vista. */
  vacios: string[];
  /** Los valores de las cifras (`.kpi-val`) tal como se leen. */
  cifras: string[];
  /** Campos que se pueden rellenar a la vista: un formulario enseña eso. */
  campos?: number;
  /**
   * La página consultó `plane.usuario`: lo que enseña depende de quien entra,
   * y vacía con los ojos de quien construye puede ser lo correcto.
   */
  usuario?: boolean;
  /** El comienzo del texto visible, para que la IA vea lo que se ve. */
  texto: string;
  /**
   * Cajas hermanas --tarjetas, botones, campos-- que se tocan sin ningún
   * espacio entre ellas, medidas sobre lo dibujado. El hueco entre piezas
   * repetidas lo pone quien las contiene, y un contenedor propio sin `gap`
   * no lo pone: el código no lo dice y la consola tampoco.
   */
  pegados?: ProbeGlued[];
  /** Cajas con su texto pegado al borde: sin relleno por dentro. */
  alBorde?: ProbeEdge[];
}

/** Un grupo de cajas pegadas: cuáles, dentro de qué y en qué sentido. */
export interface ProbeGlued {
  caja: string;
  dentro: string;
  eje: "vertical" | "horizontal";
  /** Cuántos pares de ese mismo tipo se tocan. */
  veces: number;
}

/** Una caja cuyo texto toca su borde. */
export interface ProbeEdge {
  caja: string;
  texto: string;
  lado: "arriba" | "derecha" | "abajo" | "izquierda";
  veces: number;
}

/** Lo que responde el diálogo de impacto una vez decidido. */
export interface ImpactResult {
  message: string;
  steps: AiStep[];
  /** Páginas que la IA reescribio, si se autorizó. */
  fixed: string[];
}

/* ------------------------------------------------------------------ */
/* Conectores                                                           */
/* ------------------------------------------------------------------ */

/**
 * Cómo se identifica Planer ante la API de un conector: sin clave, con un token
 * Bearer o con usuario y contraseña (autenticación básica). No hay OAuth.
 */
export type ConnectorAuthKind = "ninguna" | "bearer" | "basica";
export const CONNECTOR_AUTH_KINDS = [
  "ninguna",
  "bearer",
  "basica",
] as const satisfies readonly ConnectorAuthKind[];

/** Los métodos que puede usar una operación. */
export type ConnectorMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export const CONNECTOR_METHODS = [
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
] as const satisfies readonly ConnectorMethod[];

/** Dónde viaja un parámetro: dentro de la ruta, en la consulta o en el cuerpo JSON. */
export type ConnectorParamPlace = "ruta" | "consulta" | "cuerpo";
export const CONNECTOR_PARAM_PLACES = [
  "ruta",
  "consulta",
  "cuerpo",
] as const satisfies readonly ConnectorParamPlace[];

/** El tipo de un parámetro: decide cómo se convierte el valor antes de enviarlo. */
export type ConnectorParamType = "texto" | "numero" | "fecha" | "sino";
export const CONNECTOR_PARAM_TYPES = [
  "texto",
  "numero",
  "fecha",
  "sino",
] as const satisfies readonly ConnectorParamType[];

/** Un parámetro de una operación. */
export interface ConnectorParam {
  name: string;
  in: ConnectorParamPlace;
  type: ConnectorParamType;
  required: boolean;
  description: string;
  /** El valor que usa «Probar». Va como texto, igual que se escribe en el formulario. */
  example: string;
}

/** Una operación de un conector: una llamada concreta a su API. */
export interface ConnectorOperation {
  /** Nombre técnico, único dentro del conector: `listar_clientes`. */
  name: string;
  description: string;
  method: ConnectorMethod;
  /** Ruta que se añade a la dirección base, con `{parametro}` donde va uno de ruta. */
  path: string;
  /** Escribe en el sistema externo. Por defecto, todo lo que no es GET. */
  write: boolean;
  params: ConnectorParam[];
  /** Una respuesta de ejemplo, como texto (normalmente JSON). */
  sampleResponse: string;
}

/** Un encabezado personalizado, tal como lo ve el panel: el valor secreto no viaja. */
export interface ConnectorHeaderView {
  name: string;
  /** Vacío si es secreto: el valor guardado no sale del servidor. */
  value: string;
  secret: boolean;
  /** Tiene un valor guardado (solo dice algo en los secretos). */
  hasValue: boolean;
}

/** Un conector, tal como lo ve el panel. Nunca lleva el secreto: solo si lo tiene. */
export interface ConnectorInfo {
  id: string;
  name: string;
  description: string;
  /** El icono elegido (un nombre de la fuente), o vacío para el de siempre. */
  icon: string;
  baseUrl: string;
  auth: {
    kind: ConnectorAuthKind;
    /** El usuario de la autenticación básica. No es secreto. */
    user: string;
    /** Hay un token o una contraseña guardados. */
    hasSecret: boolean;
  };
  headers: ConnectorHeaderView[];
  operations: ConnectorOperation[];
  /** Modo de depuración: registra las peticiones y no envía las escrituras. */
  debug: boolean;
  created?: string;
  updated?: string;
}

/**
 * Lo que manda el formulario al guardar un conector. Un secreto vacío quiere
 * decir «el que ya estaba guardado».
 */
export interface ConnectorInput {
  name: string;
  description: string;
  /** Sin él se conserva el que tenía (el asistente IA no lo devuelve). */
  icon?: string;
  baseUrl: string;
  auth: { kind: ConnectorAuthKind; user?: string; secret?: string };
  headers: { name: string; value: string; secret: boolean }[];
  operations: ConnectorOperation[];
}

/**
 * Por qué está activo un conector en una aplicación: `app` si se activó en
 * ella directamente, o el id del usuario del panel que lo trajo.
 */
export interface ConnectorActivation {
  connectorId: string;
  via: string;
}

/** Una aplicación donde está activo un conector, vista desde el conector. */
export interface ConnectorAppUse {
  id: string;
  name: string;
  slug: string;
  icon: string;
  theme?: AppTheme | null;
  /** `app` o el id del usuario del panel que lo trajo. */
  via: string;
  /** Cómo se dice el motivo: «asignado a esta aplicación» o el nombre de la persona. */
  viaName: string;
  /** Cuántas páginas de esa aplicación lo llaman. */
  pages: number;
}

/** Un usuario del panel al que está asignado un conector. */
export interface ConnectorUser {
  id: string;
  name: string;
  email: string;
  /**
   * En cuántas aplicaciones entra --las creó o las edita--: donde se activaría
   * al asignárselo. Solo en la lista para asignar.
   */
  apps?: number;
}

/** Llamadas y errores de un periodo. */
export interface ConnectorCount {
  calls: number;
  errors: number;
  /** Tiempo medio de respuesta, en milisegundos. */
  avgMs: number;
}

/** Una fila del listado de conectores. */
export interface ConnectorSummary extends ConnectorInfo {
  users: ConnectorUser[];
  apps: ConnectorAppUse[];
  today: ConnectorCount;
}

/** Lo que enseña la pestaña «Conectores»: los conectores y con qué filtrar. */
export interface ConnectorsView {
  connectors: ConnectorSummary[];
  /** Los usuarios del panel, para asignar y filtrar. */
  builders: ConnectorUser[];
  /** Todas las aplicaciones, para activar y filtrar. */
  apps: Pick<AppRecord, "id" | "name" | "slug" | "icon" | "theme" | "owner">[];
  /** La IA de la instancia está activa: hay asistente en el formulario. */
  aiEnabled: boolean;
}

/** Lo que responde `POST /api/conectores/importar`. */
export interface ConnectorImportResult {
  /** Los que entraron, con el nombre con que quedaron guardados. */
  imported: {
    id: string;
    name: string;
    /** El nombre del archivo, si hubo que cambiarlo porque ya existía. */
    renamedFrom?: string;
    /** Le falta la clave o un encabezado secreto: el archivo no los trae. */
    missingSecret: boolean;
  }[];
  /** Los que no entraron, con el motivo. */
  failed: { name: string; message: string }[];
}

/** Una petición o un fallo registrados de un conector. */
export interface ConnectorLogEntry {
  id: string;
  kind: "depuracion" | "error";
  at: string;
  operation: string;
  method: string;
  /** La dirección completa, sin valores secretos. */
  url: string;
  params: Record<string, unknown>;
  body: unknown;
  app: string;
  appName: string;
  page: string;
  pageName: string;
  /** La persona o el origen: «Ajustes › Probar», «Chat de IA», un correo. */
  who: string;
  /** Llegó a la API. Una escritura en depuración no llega. */
  sent: boolean;
  status: number;
  message: string;
}

/** Cómo se agrupa la gráfica de uso. */
export type ConnectorStatsUnit = "dia" | "mes";

/** Las estadísticas de uso de un conector en un periodo. */
export interface ConnectorStats {
  unit: ConnectorStatsUnit;
  /** `AAAA-MM` por día o `AAAA` por mes. */
  period: string;
  buckets: ({ key: string; label: string } & ConnectorCount)[];
  totals: ConnectorCount;
  byOperation: ({ operation: string } & ConnectorCount)[];
  byApp: ({ app: string; appName: string } & ConnectorCount)[];
}

/** La vista de un conector: sus datos, a quién llega y lo que registró. */
export interface ConnectorDetail extends ConnectorSummary {
  log: ConnectorLogEntry[];
  errors: ConnectorLogEntry[];
}

/** Lo que devuelve «Probar» una operación. */
export interface ConnectorCallResult {
  /** Llegó a la API. En depuración, una escritura no llega. */
  sent: boolean;
  ok: boolean;
  status: number;
  ms: number;
  /** La dirección que se llamó, sin valores secretos. */
  url: string;
  body: unknown;
  /** Por qué falló, sin valores secretos. */
  error?: string;
}

/** Un conector activo en una aplicación, como lo ve quien la construye. */
export interface AppConnector {
  id: string;
  name: string;
  description: string;
  icon: string;
  via: string;
  viaName: string;
  operations: Pick<ConnectorOperation, "name" | "description" | "write" | "method">[];
}

/**
 * Código del error de una página que llama a un conector que ya no está activo
 * en su aplicación. El panel pone un aviso dentro de la página.
 */
export const CONNECTOR_UNAVAILABLE = "conector_no_disponible";

/** Código del error de escribir en un conector que está en modo de depuración. */
export const CONNECTOR_IN_REVIEW = "en_revision";

/** Código del error de una API externa que respondió mal o no respondió. */
export const CONNECTOR_FAILED = "servicio_externo";

/** Lo que se lee al intentar escribir en un conector en depuración. */
export const CONNECTOR_IN_REVIEW_MESSAGE =
  "No se guardó: este servicio está en revisión. Intenta más tarde.";
